import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import { MultiselectOption } from '@cbo/shared-library';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import StarRateIcon from '@mui/icons-material/StarRate';
import { useTheme } from '@mui/material/styles';
import { isEqual } from 'lodash';
import { filtersContainerSx, innerRowSx, filterItemBoxSx, makeScrollGridSx } from './styles';
import Filter from './Filter';
import { DatasetColumn } from '../../CentralReports/components/CreateReportDialog';
import DateFilter from '../../../components/GlobalFilterBar/DateFilter/DateFilter';
import { yesterday } from '../../../utils/reportingUtils/filterUtils';
import SiteFilter from '../../../components/GlobalFilterBar/SiteFilter/SiteFilter';
import { ReportFiltersFormValues } from '../types';

type DynamicFilterFields = Record<`filter_${string}`, MultiselectOption[]>;

interface ReportFiltersProps {
  filters: DatasetColumn[];
  onFilterChange: (values: ReportFiltersFormValues & DynamicFilterFields) => void;
  reportFilters: ReportFiltersFormValues | undefined;
}

/**
 * Component to render report filters including site, date range, and dynamic filters.
 * @param filters The array of DatasetColumn objects representing dynamic filters.
 * @param onFilterChange Optional callback function called when filter values change.
 * @param reportFilters The current filter values from parent component.
 * @returns A JSX element representing the report filters form.
 */
function ReportFilters({ filters, onFilterChange, reportFilters }: ReportFiltersProps) {
  // Add controllers for every passed filter to form context's default values
  const dynamicDefaults = useMemo<DynamicFilterFields>(
    () => Object.fromEntries(filters.map((f) => [`filter_${f.id}`, [] as MultiselectOption[]])) as DynamicFilterFields,
    [filters]
  );
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const scrollByAmount = 360; // roughly 3-4 chips width
  const scrollLeft = () => scrollRef.current?.scrollBy({ left: -scrollByAmount, behavior: 'smooth' });
  const scrollRight = () => scrollRef.current?.scrollBy({ left: scrollByAmount, behavior: 'smooth' });
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const theme = useTheme();

  // Update scroll button visibility based on scroll position
  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < maxScrollLeft);
  };

  // Initial check and event listener for scroll updates
  useEffect(() => {
    updateScrollButtons();
    const handleResize = () => updateScrollButtons();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const filterFormContext = useForm<ReportFiltersFormValues & DynamicFilterFields>({
    mode: 'onChange',
    defaultValues: {
      sites: [],
      dateRange: {
        startDate: yesterday,
        endDate: yesterday,
      },
    },
  });
  const { watch } = filterFormContext;
  const filterValues: ReportFiltersFormValues & DynamicFilterFields = watch();
  const uniqueFilters = Array.from(new Map(filters.map((filter) => [filter.filterType, filter])).values());

  useEffect(() => {
    if (isEqual(reportFilters, filterValues)) {
      return;
    }
    onFilterChange(filterValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues]);

  useEffect(() => {
    const currentValues = filterFormContext.getValues();
    const injectedDefaults = { ...currentValues, ...dynamicDefaults };
    filterFormContext.reset(injectedDefaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, dynamicDefaults]);

  return (
    <FormProvider {...filterFormContext}>
      <FormContainer formContext={filterFormContext}>
        <Box sx={filtersContainerSx}>
          {canScrollLeft && (
            <IconButton aria-label='scroll-left' onClick={scrollLeft} size='small'>
              <StarRateIcon />
            </IconButton>
          )}
          <Grid
            ref={scrollRef}
            sx={makeScrollGridSx(theme, canScrollLeft, canScrollRight)}
            role='grid'
            onScroll={updateScrollButtons}
          >
            <Grid container gap={2} sx={innerRowSx} direction='row' wrap='nowrap'>
              <Box sx={filterItemBoxSx}>
                <SiteFilter controllerName='sites' />
              </Box>
              <Box sx={filterItemBoxSx}>
                <DateFilter predefined useChips controllerName='dateRange' onlyFilterContainer={false} />
              </Box>
              {uniqueFilters.map(
                (f) =>
                  filterFormContext.getValues(`filter_${f.id}`) && (
                    <Box key={f.id} sx={filterItemBoxSx}>
                      <Filter filter={f} />
                    </Box>
                  )
              )}
            </Grid>
          </Grid>
          {canScrollRight && (
            <IconButton aria-label='scroll-right' onClick={scrollRight} size='small'>
              <StarRateIcon />
            </IconButton>
          )}
        </Box>
      </FormContainer>
    </FormProvider>
  );
}

export default ReportFilters;
